import { useRef, useState, useCallback, useEffect } from "react";
import { ChevronsLeftRight } from "lucide-react";

/**
 * Single interactive Before/After comparison.
 * Drag the gold handle (or move your mouse over) to reveal the "after" state.
 */
export default function BeforeAfterCompare({
  beforeSrc,
  afterSrc,
  beforeLabel = "Before",
  afterLabel = "After",
  caption,
  testid,
}) {
  const wrapperRef = useRef(null);
  const [pos, setPos] = useState(50); // percentage
  const [dragging, setDragging] = useState(false);

  const setFromClientX = useCallback((clientX) => {
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setPos((x / rect.width) * 100);
  }, []);

  const onPointerDown = (e) => {
    e.preventDefault();
    setDragging(true);
    setFromClientX(e.clientX);
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => setFromClientX(e.clientX);
    const up = () => setDragging(false);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [dragging, setFromClientX]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 3));
    if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 3));
  };

  return (
    <figure
      data-testid={testid}
      className="reveal relative w-full overflow-hidden rounded-sm border border-white/10 bg-black select-none"
    >
      <div
        ref={wrapperRef}
        className="relative w-full aspect-square md:aspect-[4/3] cursor-ew-resize touch-none"
        onPointerDown={onPointerDown}
      >
        {/* AFTER (full background) */}
        <img
          src={afterSrc}
          alt={`${caption || "Detail"} - after`}
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />
        <span className="absolute top-4 right-4 z-20 text-[10px] uppercase tracking-[0.24em] bg-gold text-black px-3 py-1 rounded-full font-medium">
          {afterLabel}
        </span>

        {/* BEFORE (clipped layer) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${pos}%` }}
          aria-hidden="true"
        >
          <img
            src={beforeSrc}
            alt={`${caption || "Detail"} - before`}
            className="absolute inset-0 h-full w-full object-cover"
            style={{ width: `${100 / Math.max(pos, 0.1) * 100}%`, maxWidth: "none" }}
            draggable={false}
          />
          <span className="absolute top-4 left-4 z-20 text-[10px] uppercase tracking-[0.24em] bg-black/70 text-white border border-white/30 px-3 py-1 rounded-full">
            {beforeLabel}
          </span>
        </div>

        {/* Handle */}
        <div
          className="absolute top-0 bottom-0 z-30 flex items-center justify-center pointer-events-none"
          style={{ left: `calc(${pos}% - 1px)` }}
        >
          <div className="w-px h-full bg-gold/90 shadow-[0_0_18px_rgba(212,175,55,0.6)]" />
          <button
            type="button"
            tabIndex={0}
            onKeyDown={onKeyDown}
            data-testid={testid ? `${testid}-handle` : undefined}
            aria-label="Drag to compare before and after"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pos)}
            role="slider"
            className="pointer-events-auto absolute h-12 w-12 -ml-6 grid place-items-center rounded-full bg-gold text-black border-2 border-black shadow-[0_8px_28px_-4px_rgba(212,175,55,0.55)] focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-black"
          >
            <ChevronsLeftRight size={20} strokeWidth={2.4} />
          </button>
        </div>
      </div>

      {caption && (
        <figcaption className="flex items-center justify-between gap-4 px-5 py-4 border-t border-white/10 bg-[#0a0a0a]">
          <span className="font-display text-lg text-white">{caption}</span>
          <span className="text-[10px] uppercase tracking-[0.24em] text-gold/80">
            Real client · Real result
          </span>
        </figcaption>
      )}
    </figure>
  );
}
